/* eslint-disable import/no-extraneous-dependencies */
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useIntercom } from "react-use-intercom";
// Imported for its global `window.Intercom` type augmentation used below.
import "@intercom/messenger-js-sdk";
import { ApplicationFormSlice, UserDetailsState } from "@/store/slices/types/applicationFormSlice";
// @ts-expect-error: ignoring as it is existing js file
import { userDetailsSelector } from "@/store/slices/applicationFormSlice";
// @ts-expect-error: ignoring as it is existing js file
import { userSelector } from "@/store/slices/userSlice";

type IntercomUser = {
  _id?: string;
  firstName?: string;
  lastName?: string;
  email?: { address?: string };
  organisation?: { name?: string; workOSId?: string };
};

export default function IntercomChat() {
  const { boot, update, shutdown } = useIntercom();
  const { isIntercomChatOptions } = useSelector<ApplicationFormSlice, UserDetailsState>(userDetailsSelector);
  const user = useSelector(userSelector) as IntercomUser;

  // Boot Intercom with the logged-in user's identity (and their organisation as
  // the company/tenancy). Intercom cannot promote an anonymous lead into an
  // identified user via update(), so we must boot WITH the userId up-front -
  // otherwise the visitor keeps a random name like "Cyan Megaphone from Australia"
  // and no company is attached.
  useEffect(() => {
    const organisation = user?.organisation;
    const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
    // Display as "First Last - Organisation" so the tenancy is visible alongside the name.
    const name = [fullName, organisation?.name].filter(Boolean).join(" - ").trim();

    if (!user?._id) {
      boot();
      return () => shutdown();
    }

    const identity = {
      userId: user._id,
      name: name || undefined,
      email: user?.email?.address,
      company: organisation?.workOSId
        ? { companyId: organisation.workOSId, name: organisation?.name }
        : undefined,
    };

    boot(identity);
    // boot() only sets attributes when the contact is first created; for users
    // who already chatted (so already exist in Intercom) we must update() to
    // push the new name/company onto their existing record.
    update(identity);

    return () => shutdown();
  }, [
    boot,
    update,
    shutdown,
    user?._id,
    user?.firstName,
    user?.lastName,
    user?.email?.address,
    user?.organisation?.workOSId,
    user?.organisation?.name,
  ]);

  // When a service issue is flagged, auto-open the messenger with a prefilled
  // message. Intercom is already booted (and identified) above, so we only need
  // to drive the widget here.
  useEffect(() => {
    if (!isIntercomChatOptions?.showIntercom || !isIntercomChatOptions?.intercomData) return undefined;

    // Small delay to ensure Intercom is fully loaded.
    const timer = setTimeout(() => {
      if (window.Intercom) {
        window.Intercom(
          "showNewMessage",
          `Hey it looks like you're having an issue${isIntercomChatOptions?.service ? ` with ${isIntercomChatOptions.service}.` : "."} We're looking into it as we speak and we'll comeback to you ASAP. In the meantime, if you have any questions, feel free to reply here.`,
        );
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [isIntercomChatOptions]);

  return null;
}
